import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import { RangerAvatar } from '../components/RangerAvatar';
import {
    Leaf,
    Star,
    Trophy,
    Target,
    Medal,
    Lightning,
    Binoculars,
    House,
    BookOpen,
    UserCircle,
    Lock,
    X,
    PencilSimple,
    Check,
    GraduationCap
} from '@phosphor-icons/react';

const AVATARS = [
    { id: 'koala', emoji: '🐨', name: 'Koala' },
    { id: 'kangaroo', emoji: '🦘', name: 'Kangaroo' },
    { id: 'wombat', emoji: '🐻', name: 'Wombat' },
    { id: 'platypus', emoji: '🦆', name: 'Platypus' },
    { id: 'emu', emoji: '🐦', name: 'Emu' },
    { id: 'echidna', emoji: '🦔', name: 'Echidna' },
    { id: 'quokka', emoji: '🐹', name: 'Quokka' },
    { id: 'dingo', emoji: '🐕', name: 'Dingo' },
    { id: 'kookaburra', emoji: '🐤', name: 'Kookaburra' },
    { id: 'turtle', emoji: '🐢', name: 'Sea Turtle' },
    { id: 'devil', emoji: '😈', name: 'Tassie Devil' },
    { id: 'cockatoo', emoji: '🦜', name: 'Cockatoo' }
];

const getAvatarEmoji = (avatarId) => {
    const avatar = AVATARS.find(a => a.id === avatarId);
    return avatar?.emoji || '🌿';
};

const getLevelColor = (level) => {
    const colors = {
        'Rookie Ranger': 'text-[#22c55e]',
        'Junior Ranger': 'text-[#38bdf8]',
        'Expert Explorer': 'text-purple-500',
        'Master Ranger': 'text-orange-500',
        'Elite Guardian': 'text-[#fb7185]'
    };
    return colors[level] || 'text-[#22c55e]';
};

const getXpForNextLevel = (level) => {
    const thresholds = {
        'Rookie Ranger': 500,
        'Junior Ranger': 1500,
        'Expert Explorer': 3000,
        'Master Ranger': 5000,
        'Elite Guardian': 10000
    };
    return thresholds[level] || 500;
};

export default function ChildDashboard() {
    const { user, api, updateUser } = useAuth();
    const navigate = useNavigate();
    const [missions, setMissions] = useState([]);
    const [completedMissions, setCompletedMissions] = useState([]);
    const [badges, setBadges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showConfetti, setShowConfetti] = useState(false);
    
    // Parent access modal
    const [showParentAccess, setShowParentAccess] = useState(false);
    const [parentPin, setParentPin] = useState('');
    const [pinError, setPinError] = useState('');

    useEffect(() => {
        fetchDashboardData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchDashboardData = async () => {
        try {
            const [missionsRes, completedRes, badgesRes] = await Promise.all([
                api().get('/missions'),
                api().get('/missions/user/completed'),
                api().get('/badges')
            ]);
            setMissions(missionsRes.data.slice(0, 3));
            setCompletedMissions(completedRes.data);
            setBadges(badgesRes.data);
        } catch (error) {
            console.error('Error fetching dashboard:', error);
            toast.error('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    };

    const handleParentAccess = async () => {
        setPinError('');
        try {
            // Verify parent password/PIN
            const response = await api().post('/auth/verify-parent', { password: parentPin });
            if (response.data.success) {
                // Switch back to parent account
                const parentToken = response.data.access_token;
                localStorage.setItem('token', parentToken);
                window.location.href = '/dashboard';
            }
        } catch (error) {
            setPinError('Incorrect password. Please try again.');
        }
    };

    const xpProgress = user?.xp ? (user.xp / getXpForNextLevel(user.level)) * 100 : 0;

    if (loading) {
        return (
            <div className="min-h-screen bg-[hsl(53,92%,96%)] flex items-center justify-center">
                <div className="brutalist-card p-8 text-center">
                    <div className="w-16 h-16 border-4 border-[#22c55e] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                    <p className="font-fredoka text-lg">Loading your adventure...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[hsl(53,92%,96%)] dot-grid">
            {showConfetti && <Confetti recycle={false} numberOfPieces={200} />}
            
            {/* Top Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-white border-b-4 border-[#1e293b]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
                            <div className="w-10 h-10 rounded-full bg-[#22c55e] border-4 border-[#1e293b] flex items-center justify-center">
                                <Leaf weight="fill" className="text-white" size={20} />
                            </div>
                            <span className="font-fredoka text-xl font-bold text-[#1e293b] hidden sm:block">
                                Bush Rangers
                            </span>
                        </Link>
                        
                        <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-[#fde047] rounded-full border-4 border-[#1e293b]" data-testid="token-balance-display">
                                <Star weight="fill" className="text-[#1e293b]" size={20} />
                                <span className="font-fredoka font-bold">{user?.tokens_balance || 0} Tokens</span>
                            </div>
                            <Link
                                to="/my-classroom"
                                className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#22c55e]/10 hover:bg-[#22c55e]/20 border-2 border-[#22c55e]"
                                data-testid="classroom-btn"
                            >
                                <GraduationCap weight="bold" size={20} className="text-[#22c55e]" />
                                <span className="hidden sm:inline text-sm font-bold text-[#22c55e]">Classroom</span>
                            </Link>
                            <motion.button
                                onClick={() => setShowParentAccess(true)}
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                className="flex items-center gap-2 px-3 py-2 rounded-full bg-[#f1f5f9] hover:bg-[#e2e8f0] border-2 border-[#1e293b]/20"
                                data-testid="parent-access-btn"
                            >
                                <Lock weight="bold" size={20} className="text-[#475569]" />
                                <span className="hidden sm:inline text-sm font-semibold text-[#475569]">Parent</span>
                            </motion.button>
                        </div>
                    </div>
                </div>
            </nav>

            {/* Main Content */}
            <main className="pt-20 px-4 max-w-7xl mx-auto">
                {/* Welcome Section */}
                <div className="grid md:grid-cols-12 gap-6 mb-8">
                    {/* Hero Card */}
                    <motion.div 
                        className="md:col-span-8 brutalist-card p-6 md:p-8 bg-gradient-to-br from-[#22c55e] to-[#16a34a]"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                    >
                        <div className="flex flex-col md:flex-row items-center gap-6">
                            {/* Clickable Avatar */}
                            <div 
                                className="avatar-ring cursor-pointer relative group"
                                onClick={() => navigate('/customize')}
                                data-testid="avatar-edit-btn"
                            >
                                <div className="w-24 h-24 md:w-32 md:h-32 rounded-full bg-white overflow-hidden border-4 border-[#1e293b]">
                                    <RangerAvatar config={user?.avatar_config} variant="badge" className="w-full h-full" data-testid="dashboard-avatar" />
                                </div>
                                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                    <PencilSimple weight="bold" className="text-white" size={32} />
                                </div>
                            </div>
                            <div className="text-center md:text-left text-white">
                                <h1 className="font-fredoka text-2xl md:text-4xl font-bold mb-2">
                                    G'day, {user?.name}!
                                </h1>
                                <p className={`font-fredoka text-xl ${getLevelColor(user?.level)} bg-white/20 px-4 py-1 rounded-full inline-block`}>
                                    {user?.level || 'Rookie Ranger'}
                                </p>
                                <div className="mt-4">
                                    <div className="flex items-center justify-center md:justify-start gap-2 mb-2">
                                        <Lightning weight="fill" size={20} />
                                        <span className="font-semibold">{user?.xp || 0} / {getXpForNextLevel(user?.level)} XP</span>
                                    </div>
                                    <div className="progress-bar w-full max-w-xs bg-white/30">
                                        <div 
                                            className="progress-bar-fill bg-[#fde047]" 
                                            style={{ width: `${Math.min(xpProgress, 100)}%` }}
                                        ></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </motion.div>

                    {/* Stats Card */}
                    <motion.div 
                        className="md:col-span-4 brutalist-card p-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <h3 className="font-fredoka text-lg font-bold text-[#1e293b] mb-4">Your Stats</h3>
                        <div className="space-y-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#22c55e]/10 flex items-center justify-center">
                                        <Target weight="duotone" className="text-[#22c55e]" size={24} />
                                    </div>
                                    <span className="font-semibold">Missions Done</span>
                                </div>
                                <span className="font-fredoka text-2xl font-bold text-[#22c55e]">
                                    {completedMissions.length}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#fde047]/20 flex items-center justify-center">
                                        <Medal weight="duotone" className="text-[#f59e0b]" size={24} />
                                    </div>
                                    <span className="font-semibold">Badges Earned</span>
                                </div>
                                <span className="font-fredoka text-2xl font-bold text-[#f59e0b]">
                                    {user?.badges?.length || 0}
                                </span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl bg-[#38bdf8]/10 flex items-center justify-center">
                                        <Star weight="fill" className="text-[#38bdf8]" size={24} />
                                    </div>
                                    <span className="font-semibold">Bush Tokens</span>
                                </div>
                                <span className="font-fredoka text-2xl font-bold text-[#38bdf8]">
                                    {user?.tokens_balance || 0}
                                </span>
                            </div>
                        </div>
                    </motion.div>
                </div>

                {/* Quick Links - Animals & Quizzes */}
                <motion.section 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.15 }}
                >
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        <Link to="/animals" data-testid="quick-link-animals">
                            <div className="brutalist-card p-4 text-center hover:bg-[#38bdf8]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🐨</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Animal Library</p>
                                <p className="text-xs text-[#475569]">Meet Aussie animals!</p>
                            </div>
                        </Link>
                        <Link to="/habitats" data-testid="quick-link-habitats">
                            <div className="brutalist-card p-4 text-center hover:bg-[#22c55e]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🌏</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Explore Habitats</p>
                                <p className="text-xs text-[#475569]">Discover ecosystems!</p>
                            </div>
                        </Link>
                        <Link to="/adventure" data-testid="quick-link-adventure">
                            <div className="brutalist-card p-4 text-center hover:bg-[#16a34a]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🧭</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Adventure</p>
                                <p className="text-xs text-[#475569]">Explore near you!</p>
                            </div>
                        </Link>
                        <Link to="/games" data-testid="quick-link-games">
                            <div className="brutalist-card p-4 text-center hover:bg-[#ec4899]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🎮</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Games</p>
                                <p className="text-xs text-[#475569]">Learn & play!</p>
                            </div>
                        </Link>
                        <Link to="/content" data-testid="quick-link-quizzes">
                            <div className="brutalist-card p-4 text-center hover:bg-[#fde047]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🧠</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Quizzes</p>
                                <p className="text-xs text-[#475569]">Test your knowledge!</p>
                            </div>
                        </Link>
                        <Link to="/leaderboard" data-testid="quick-link-leaderboard">
                            <div className="brutalist-card p-4 text-center hover:bg-[#22c55e]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🏆</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Leaderboard</p>
                                <p className="text-xs text-[#475569]">See top rangers!</p>
                            </div>
                        </Link>
                        <Link to="/customize" data-testid="quick-link-customize">
                            <div className="brutalist-card p-4 text-center hover:bg-[#a855f7]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🧍</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">Customise Ranger</p>
                                <p className="text-xs text-[#475569]">Dress up your avatar!</p>
                            </div>
                        </Link>
                        <Link to="/missions" data-testid="quick-link-missions">
                            <div className="brutalist-card p-4 text-center hover:bg-[#fb7185]/10 transition-colors">
                                <span className="text-4xl mb-2 block">🎯</span>
                                <p className="font-fredoka font-bold text-[#1e293b]">All Missions</p>
                                <p className="text-xs text-[#475569]">Find new adventures!</p>
                            </div>
                        </Link>
                    </div>
                </motion.section>

                {/* Active Missions */}
                <motion.section 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 }}
                >
                    <div className="flex justify-between items-center mb-4">
                        <h2 className="font-fredoka text-2xl font-bold text-[#1e293b]">
                            Active Missions
                        </h2>
                        <Link 
                            to="/missions" 
                            className="text-[#22c55e] font-semibold hover:underline"
                            data-testid="view-all-missions"
                        >
                            View All →
                        </Link>
                    </div>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {missions.map((mission, index) => (
                            <motion.div
                                key={mission.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.1 * index }}
                            >
                                <Link to={`/missions/${mission.id}`} data-testid={`mission-card-${index}`}>
                                    <div className="brutalist-card overflow-hidden h-full">
                                        <div className="h-32 bg-gradient-to-br from-[#22c55e] to-[#16a34a] relative">
                                            {mission.image_url && (
                                                <img 
                                                    src={mission.image_url} 
                                                    alt={mission.title}
                                                    className="w-full h-full object-cover opacity-50"
                                                />
                                            )}
                                            <div className="absolute top-3 right-3">
                                                <span className={`px-3 py-1 rounded-full text-xs font-bold border-2 border-[#1e293b] ${
                                                    mission.difficulty === 'easy' ? 'bg-[#22c55e] text-white' :
                                                    mission.difficulty === 'medium' ? 'bg-[#fde047] text-[#1e293b]' :
                                                    'bg-[#fb7185] text-white'
                                                }`}>
                                                    {mission.difficulty}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="p-4">
                                            <h3 className="font-fredoka font-bold text-lg text-[#1e293b] mb-2">
                                                {mission.title}
                                            </h3>
                                            <p className="text-sm text-[#475569] line-clamp-2 mb-3">
                                                {mission.description}
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <Star weight="fill" className="text-[#fde047]" size={20} />
                                                <span className="font-fredoka font-bold text-[#1e293b]">
                                                    +{mission.xp_reward} Tokens
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Link>
                            </motion.div>
                        ))}
                    </div>
                </motion.section>

                {/* Badges Section */}
                <motion.section 
                    className="mb-8"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                >
                    <h2 className="font-fredoka text-2xl font-bold text-[#1e293b] mb-4">
                        Your Badges
                    </h2>
                    <div className="brutalist-card p-6">
                        {badges.length > 0 ? (
                            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-4">
                                {badges.map((badge) => {
                                    const isEarned = user?.badges?.includes(badge.id);
                                    return (
                                        <div 
                                            key={badge.id} 
                                            className={`text-center ${isEarned ? '' : 'opacity-40'}`}
                                            data-testid={`badge-${badge.id}`}
                                        >
                                            <div className={`w-16 h-16 mx-auto mb-2 rounded-2xl flex items-center justify-center text-2xl ${
                                                isEarned ? 'badge-earned' : 'badge-locked'
                                            }`}>
                                                {badge.icon === 'trash' && '🗑️'}
                                                {badge.icon === 'tree' && '🌳'}
                                                {badge.icon === 'droplet' && '💧'}
                                                {badge.icon === 'binoculars' && '🔭'}
                                                {badge.icon === 'shield' && '🛡️'}
                                                {badge.icon === 'star' && '⭐'}
                                            </div>
                                            <p className="font-fredoka text-xs font-semibold text-[#1e293b]">
                                                {badge.name}
                                            </p>
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <p className="text-center text-[#475569]">
                                Complete missions to earn badges!
                            </p>
                        )}
                    </div>
                </motion.section>
            </main>

            {/* Parent Access Modal */}
            {showParentAccess && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="brutalist-card p-6 w-full max-w-sm bg-white"
                    >
                        <div className="flex justify-between items-center mb-6">
                            <h2 className="font-fredoka text-xl font-bold text-[#1e293b] flex items-center gap-2">
                                <Lock weight="duotone" className="text-[#475569]" />
                                Parent Access
                            </h2>
                            <button 
                                onClick={() => { setShowParentAccess(false); setParentPin(''); setPinError(''); }}
                                className="p-2 hover:bg-gray-100 rounded-full"
                            >
                                <X weight="bold" size={24} />
                            </button>
                        </div>
                        
                        <p className="text-[#475569] text-sm mb-4">
                            Enter your parent account password to access the parent dashboard.
                        </p>

                        <input
                            type="password"
                            value={parentPin}
                            onChange={(e) => { setParentPin(e.target.value); setPinError(''); }}
                            placeholder="Enter password"
                            className="brutalist-input w-full mb-2"
                            data-testid="parent-pin-input"
                        />
                        
                        {pinError && (
                            <p className="text-[#fb7185] text-sm mb-4">{pinError}</p>
                        )}

                        <motion.button
                            onClick={handleParentAccess}
                            disabled={!parentPin}
                            whileHover={{ y: -2, x: -2 }}
                            whileTap={{ y: 2, x: 2 }}
                            className="brutalist-btn w-full bg-[#1e293b] text-white disabled:opacity-50 mt-4"
                            data-testid="verify-parent-btn"
                        >
                            Access Parent Dashboard
                        </motion.button>
                    </motion.div>
                </div>
            )}
        </div>
    );
}
